import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiClient from "../../util/apiClient";

interface InvoiceItem {
  item: string;
  quantity: string;
  rate: string;
  amount: string;
}

interface Invoice {
  id?: string;
  _id?: string;
  customerName: string;
  billTo: string;
  shipTo: string;
  date: string;
  paymentTerms: string;
  dueDate: string;
  poNumber: string;
  lineItems: InvoiceItem[];
  subtotal: number;
  status?: string;
  user?: string;
  network?: string;
  company?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface InvoiceState {
  invoices: Invoice[];
  loading: boolean;
  error: string | null;
}

const initialState: InvoiceState = {
  invoices: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchInvoicesByUser = createAsyncThunk(
  "invoice/fetchInvoicesByUser",
  async () => {
    const response = await apiClient("/invoices/getByUser", "GET");
    if (response?.success) {
      return response.result;
    }
    throw new Error(response?.message || "Failed to fetch invoices");
  }
);

export const createInvoiceAsync = createAsyncThunk(
  "invoice/createInvoice",
  async (invoiceData: Partial<Invoice>) => {
    const response = await apiClient("/invoices/", "POST", invoiceData);
    if (response?.success) {
      return response.result;
    }
    throw new Error(response?.message || "Failed to create invoice");
  }
);

export const updateInvoiceAsync = createAsyncThunk(
  "invoice/updateInvoice",
  async ({ id, data }: { id: string; data: Partial<Invoice> }) => {
    const response = await apiClient(`/invoices/${id}`, "PUT", data);
    if (response?.success) {
      return response.result;
    }
    throw new Error(response?.message || "Failed to update invoice");
  }
);

export const deleteInvoiceAsync = createAsyncThunk(
  "invoice/deleteInvoice",
  async (id: string) => {
    const response = await apiClient(`/invoices/${id}`, "DELETE");
    if (response?.success) {
      return id;
    }
    throw new Error(response?.message || "Failed to delete invoice");
  }
);

const invoiceSlice = createSlice({
  name: "invoice",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setInvoices: (state, action: PayloadAction<Invoice[]>) => {
      state.invoices = action.payload;
    },
    addInvoice: (state, action: PayloadAction<Invoice>) => {
      state.invoices.unshift(action.payload);
    },
    updateInvoice: (state, action: PayloadAction<Invoice>) => {
      const index = state.invoices.findIndex(
        (invoice) => invoice._id === action.payload._id || invoice.id === action.payload.id
      );
      if (index !== -1) {
        state.invoices[index] = action.payload;
      }
    },
    removeInvoice: (state, action: PayloadAction<string>) => {
      state.invoices = state.invoices.filter(
        (invoice) => invoice._id !== action.payload && invoice.id !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
 
      .addCase(fetchInvoicesByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoicesByUser.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload;
      })
      .addCase(fetchInvoicesByUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch invoices";
      })
      // Create invoice
      .addCase(createInvoiceAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInvoiceAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices.unshift(action.payload);
      })
      .addCase(createInvoiceAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create invoice";
      })
      // Update invoice
      .addCase(updateInvoiceAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvoiceAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.invoices.findIndex(
          (invoice) => invoice._id === action.payload._id || invoice.id === action.payload.id
        );
        if (index !== -1) {
          state.invoices[index] = action.payload;
        }
      })
      .addCase(updateInvoiceAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update invoice";
      })
 
      .addCase(deleteInvoiceAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteInvoiceAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = state.invoices.filter(
          (invoice) => invoice._id !== action.payload && invoice.id !== action.payload
        );
      })
      .addCase(deleteInvoiceAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete invoice";
      });
  },
});

export const {
  clearError,
  setInvoices,
  addInvoice,
  updateInvoice,
  removeInvoice,
} = invoiceSlice.actions;

export default invoiceSlice.reducer;